import React, { useEffect, useState } from "react";
import "./customerDashboardStyle.css";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import ProfileImage from "../../../../components/ProfileImage";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { getUserInfo } from "../../../../reducers/authSlice";
import HTTPService, { fetchSubmissionRate } from "../../../../utils/axios";
import { customToast } from "../../../../App";
import RewardManagementChart from "./RewardManagementChartComponent/RewardManagementChart";
import { fetchWalletStats } from "../../../../utils/axios";
import { Folder, PlayCircle, Wallet2Icon, Users2 } from "lucide-react";

import ProgramSummary from "./ProgramSummaryComponent/ProgramSummary";
import CustomerProgram from "./CustomerProgramComponents/CustomerProgram";
import VulnerabilityChartDashboard from "./VulnerabilitySummaryComponents/VulnerabilityChartDashboard";
import ProgramStatsGaugeChart from "./ProgramStats/ProgramStatsGaugeChart";
import ProgramSummaryTable from "./CustomerProgramComponents/ProgramSummaryTable";
import ResearcherLeaderBoard from "./ResearcherLeaderboard/ResearcherLeaderboard";
import useCountAnimation from "./Animation/UseCountAnimation";

const severityMapping = {
  Critical: 4,
  High: 3,
  Severe: 2,
  Low: 1,
};

const data = [
  {
    name: "XSS",
    program1: severityMapping["Critical"],
    program2: severityMapping["Low"],
    amt: 2400,
  },
  {
    name: "Sql Injection",
    program1: severityMapping["Severe"],
    program2: severityMapping["High"],
    amt: 2210,
  },
  {
    name: "DOS",
    program1: severityMapping["Low"],
    program2: severityMapping["Critical"],
    amt: 2290,
  },
  {
    name: "ClickBait",
    program1: severityMapping["High"],
    program2: severityMapping["Severe"],
    amt: 2000,
  },
];

const reverseMapping = { 1: "Low", 2: "Severe", 3: "High", 4: "Critical" };

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-4 bg-white flex flex-col gap-4 rounded-md">
        <p className="text-medium text-lg">{label}</p>
        <p className="text-sm text-blue-400">
          Program 1:
          <span className="ml-2">{reverseMapping[payload[0].value]}</span>
        </p>
        <p className="text-sm text-indigo-400">
          Program 2:
          <span className="ml-2">{reverseMapping[payload[1].value]}</span>
        </p>
      </div>
    );
  }
};

const Breadcrumbname = {
  home: "/customer/home",
  Dashboard: "",
};
const severityValues = {
  Critical: 5,
  Severe: 4,
  Moderate: 3,
  Low: 2,
  Informational: 1,
  null: 0,
};
const CustomerDashboard = () => {
  const ASSET_URL = process.env.REACT_APP_ASSET_URL;
  const { invokeApi } = HTTPService();
  const { userInfo } = useSelector(getUserInfo);
  const [customerPortfolioLoad, setCustomerPortfolioLoad] = useState(false);
  const [customerProgramLoad, setCustomerProgramLoad] = useState(false);
  const [researcherPortfolioLoad, setResearcherPortfolioLoad] = useState(false);
  const [customerPortfolio, setCustomerPortfolio] = useState([]);
  const [researcherPortfolio, setResearcherPortfolio] = useState([]);
  const [customerPrograms, setCustomerPrograms] = useState([]);
  const [programStats, setProgramStats] = useState([]);
  const [totalSubmitters, setTotalSubmitters] = useState([]);
  const [issueTracking, setIssueTracking] = useState([]);
  const [rewardManagement, setRewardmanagement] = useState([]);
  const [widgetStats, setWidgetStats] = useState({});
  const [submissionRate, setSubmissionRate] = useState([]);
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const animatedTotalPrograms = useCountAnimation(
    widgetStats.total_programs || 0
  );
  const animatedActivePrograms = useCountAnimation(
    widgetStats.total_active_programs || 0
  );
  const animatedWalletBalance = useCountAnimation(
    parseFloat(walletData?.Wallet_Balance) || 0,
    1500
  ); // Adjust duration as needed
  const animatedTotalSubmitters = useCountAnimation(totalSubmitters || 0); // Assuming this is the correct stat

  const getCustomerPrograms = async () => {
    setCustomerProgramLoad(true);
    invokeApi({
      method: "GET",
      url: `widget/programs-customer-stats/`,
    })
      .then((data) => {
        setCustomerPrograms(data.customer_stats);
      })
      .catch((error) => {
        customToast.error("Something went wrong!", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });
      });
    setCustomerProgramLoad(false);
  };

  const getCustomerPortfolio = async () => {
    setCustomerPortfolioLoad(true);
    invokeApi({
      method: "GET",
      url: `widget/customer-portfolio/`,
    })
      .then((data) => {
        setCustomerPortfolio(
          data.sort(
            (a, b) => b.total_submissions_count - a.total_submissions_count
          )
        );
        setCustomerPortfolioLoad(false);
      })
      .catch((error) => {
        customToast.error("Something went wrong!", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });
        setCustomerPortfolioLoad(false);
      });
  };
  const getResearcherPortfolio = async () => {
    setResearcherPortfolioLoad(true);
    invokeApi({
      method: "GET",
      url: `widget/researcher-leader-board/`,
    })
      .then((data) => {
        setResearcherPortfolioLoad(false);
        setResearcherPortfolio(data);
      })
      .catch((error) => {
        customToast.error("Something went wrong!", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });
        setResearcherPortfolioLoad(false);
      });
  };

  const getTotalSubmitters = async () => {
    invokeApi({
      method: "GET",
      url: `submissions/`,
    })
      .then((data) => {
        setTotalSubmitters(data.total_submitters);
      })
      .catch((error) => {
        customToast.error("Unable to Fetch Total Submitters!", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });
        console.log(error);
      });
  };

  const getProgramStats = () => {
    invokeApi({
      method: "GET",
      url: `widget/programs-submission-stats/`,
    })
      .then((data) => {
        setProgramStats(data.program_submission_stats);
      })
      .catch((error) => {
        customToast.error("Something went wrong!", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });
      });
  };
  const getWidgetIssueTracking = () => {
    invokeApi({
      method: "GET",
      url: `widget/submission-issues-tracking/`,
    })
      .then((data) => {
        setIssueTracking(data);
      })
      .catch((error) => {
        customToast.error("Something went wrong!", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });
      });
  };
  const getWidgetRewardManagement = () => {
    invokeApi({
      method: "GET",
      url: `widget/reward-management`,
    })
      .then((data) => {
        setRewardmanagement(data);
      })
      .catch((error) => {
        customToast.error("Something went wrong!", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });
      });
  };
  const getWidgetStats = () => {
    // setResearcherPortfolioLoad(true);
    invokeApi({
      method: "GET",
      url: `widget/programs-stats/`,
    })
      .then((data) => {
        setWidgetStats(data.widget_stats);
      })
      .catch((error) => {
        // setResearcherPortfolioLoad(false);
        customToast.error("Something went wrong!", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });
      });
  };

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        const data = await fetchWalletStats();

        if (data.length > 0) {
          setWalletData(data[0]);
        } else {
          setError("No wallet data found");
        }
      } catch (err) {
        setError(err.message || "Failed to fetch wallet data");
      } finally {
        setLoading(false);
      }
    };
    fetchWalletData();
  }, []);

  const formatCurrency = (value) => {
    if (!value) return "$0.00";

    // Convert to number if it's a string
    const numValue = typeof value === "string" ? parseFloat(value) : value;

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numValue);
  };

  const walletBalance = walletData?.Wallet_Balance
    ? formatCurrency(walletData.Wallet_Balance)
    : "$0.00";

  useEffect(() => {
    if (userInfo && userInfo.user_data) {
      getCustomerPortfolio();
      getResearcherPortfolio();
      getCustomerPrograms();
      getProgramStats();
      getWidgetIssueTracking();
      getWidgetRewardManagement();
      getWidgetStats();
      getTotalSubmitters();
    }
  }, [userInfo]);

  useEffect(() => {
    const fetchSubmissionRateData = async () => {
      try {
        const data = await fetchSubmissionRate();

        const totalPrograms = data.total_programs || 0;
        const totalSubmissions = data.total_submissions || 0;
        const averageSubmissionRate =
          totalPrograms > 0
            ? `${((totalSubmissions / totalPrograms / 10) * 100).toFixed(0)}%`
            : 0;

        setSubmissionRate({ averageSubmissionRate });
      } catch (err) {
        console.error(err);
      }
    };
    fetchSubmissionRateData();
  }, []);

  return (
    <>
      <div class="container-fluid">
        <div className="row">
          <div className="col-md-12 card mt-3 top-card d-flex justify-content-around py-3">
            <div className="col-md-3 d-flex flex-column align-items-center">
              <div className="d-flex align-items-center gap-2">
                <Folder size={22} className="text-primary" />
                <span className="h5 mb-0">{animatedTotalPrograms}</span>
              </div>
              <small className="text-muted mt-1">Total Programs</small>
            </div>

            <div className="col-md-3 d-flex flex-column align-items-center">
              <div className="d-flex align-items-center gap-2">
                <PlayCircle size={22} className="text-success" />
                <span className="h5 mb-0">{animatedActivePrograms}</span>
              </div>
              <small className="text-muted mt-1">Active Programs</small>
            </div>

            <div className="col-md-3 d-flex flex-column align-items-center">
              <div className="d-flex align-items-center gap-2">
                <Wallet2Icon size={22} className="text-primary" />
                <span className="h5 mb-0">${animatedWalletBalance}</span>
              </div>
              <small className="text-muted mt-1">Wallet Balance</small>
            </div>

            <div className="col-md-3 d-flex flex-column align-items-center">
              <div className="d-flex align-items-center gap-2">
                <Users2 size={22} className="text-secondary" />
                <span className="h5 mb-0">{animatedTotalSubmitters}</span>
              </div>
              <small className="text-muted mt-1">Total Submitters</small>
            </div>
          </div>
        </div>
        <div class="row">
          <div className="ms-2 d-flex gap-4 mb-3">
            <div class="col-md-3 mt-3 card program-stats-card card-hover-effect">
              <h4 class="mt-2 text-center text-card card-subtitle text-muted mb-2">
                Average Submission Rate
              </h4>
              <ProgramStatsGaugeChart />
            </div>
            <div class="col-md-5 mt-3">
              <div class="card card-hover-effect">
                <div class="card-body vulnerability-card p-2">
                  <h4 class="text-center text-card card-subtitle text-muted">
                    Vulnerabilities Report Summary
                  </h4>
                  <div className="">
                    <VulnerabilityChartDashboard />
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-4 mt-3">
              <div class="card card-hover-effect program-summary-card shadow-sm mr-3">
                <h4 class="mt-2 text-center text-card text-muted card-subtitle">
                  Program Summary
                </h4>
                <ProgramSummary />
              </div>
            </div>
          </div>

          <div className="d-flex mb-3 gap-2">
            <div className="col-md-7 customer-program-card">
              <div class="card card-hover-effect shadow-sm">
                <CustomerProgram />
              </div>
            </div>
            <div className="col-md-6">
              <div class="card reward-management-card card-hover-effect shadow-sm">
                <RewardManagementChart />
              </div>
            </div>
          </div>
          <div className="d-flex mb-3 gap-1">
            <div className="col-md-6">
              <div
                className="card card-hover-effect shadow-sm h-80 d-flex flex-column"
                style={{ minHeight: "400px" }}
              >
                <div className="card-body d-flex flex-column">
                  <div className="flex-grow-1 overflow-auto">
                    <ResearcherLeaderBoard />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div
                className="card card-hover-effect shadow-sm h-100 d-flex flex-column"
                style={{ minHeight: "400px" }}
              >
                <div className="card-body d-flex flex-column">
                  <h5 className="card-title text-center mb-4">
                    Customer Programs
                  </h5>
                  <div className="flex-grow-1 overflow-auto">
                    <ProgramSummaryTable />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerDashboard;
